import { useCallback, useEffect, useMemo, useState } from "react";
import { Transition } from "@headlessui/react";
import _ from "lodash";

import { ReactComponent as AnnualArrowIcon } from "assets/icons/annual_discount_arrow.svg";

import Button from "components/Button";
import { useFeatureSwitch } from "components/FeatureSwitch";
import PricingPlan, { PricingPlanProps } from "components/PricingPlan";
import { PlanAttributes } from "components/PricingPlan/planFeatures";
import TaggedText from "components/TaggedText";

import Messages from "misc/Messages";

import { useUser } from "routes/dataroutes/UserData";

import { cx, fmtDate } from "utils";
import {
  getDiscountPriceFor,
  hasToContactUsBeforeChange,
  isEnterprise,
  isLegacyAnnualPlan,
  isUsingChargebeeCustomFields,
  isYearlyPlan
} from "utils/planUtils";

import Plan from "types/Plan";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useAllPlans } from "routes/data-routes/billing-data/plans";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


type PlanPairPlanType =
  Pick<Plan, "planSlug" | "planName" | "price">
  & Partial<Omit<Plan, "planSlug" | "planName" | "price">>;

type PlanPair = {
  monthly?: PlanPairPlanType;
  annual?: PlanPairPlanType;
  yearly?: PlanPairPlanType;
}

export type BillingPeriod = (keyof PlanPair) | "transitioning";


const getCustomEnterpriseFeatures = (plan: PlanPairPlanType) => {
  if (plan.customizable) {
    return plan;
  } else {
    return undefined;
  }
}

export interface PlanCardsListProps {
  planInActionIdx: number | undefined;
  setPlanInActionIdx: (idx: number | undefined) => void;
}

export default function PlanCardsList(
  {
    planInActionIdx,
    setPlanInActionIdx
  }: PlanCardsListProps
) {

  const user = useUser()!;
  const { activeSubscription } = useActiveSubscription();
  const { allPlans } = useAllPlans();
  const myPlanSlug = activeSubscription?.planSlug;
  const canHaveAffiliatePlan = user.canHaveAffiliatePlan;
  const { pricingOptions } = usePricingOptions();

  const scheduledSubscription = useMemo((): Plan | undefined => {
    if (activeSubscription?.scheduledSubscription) {
      if (activeSubscription.scheduledSubscription.customizable) {
        return {
          planSlug: activeSubscription.scheduledSubscription.planSlug,
          planName: activeSubscription.scheduledSubscription.planName || "Enterprise",
          price: activeSubscription.scheduledSubscription.price,
          currencyCode: activeSubscription.scheduledSubscription.currencyCode,
          billingPeriod: activeSubscription.scheduledSubscription.billingPeriod,
          billingPeriodUnit: activeSubscription.scheduledSubscription.billingPeriodUnit,

          apiCreditLimit: activeSubscription.scheduledSubscription.apiCreditLimit,
          apiConcurrencyLimit: activeSubscription.scheduledSubscription.apiConcurrencyLimit,
          apiGeotargeting: activeSubscription.scheduledSubscription.apiGeotargeting,

          customizable: activeSubscription.scheduledSubscription.customizable,
          hasFullAnalytics: activeSubscription.scheduledSubscription.hasFullAnalytics,
          payAsYouGoAllowed: activeSubscription.scheduledSubscription.payAsYouGoAllowed
        };
      } else {
        return allPlans?.flat()?.find(plan => plan.planSlug === activeSubscription.scheduledSubscription?.planSlug);
      }
    }

    return undefined;
  }, [ activeSubscription, allPlans ]);

  const findPlanPair = useCallback((planSlug: string) => {
    return {
      monthly: allPlans?.flat()?.find(p => p.planSlug === planSlug),
      annual: allPlans?.flat()?.find(p => p.planSlug === ("annual_" + planSlug)),
      yearly: allPlans?.flat()?.find(p => p.planSlug === ("yearly_" + planSlug)),
    };
  }, [ allPlans ]);

  const plansToShow = useMemo((): PlanPair[] => {
    const planPairs: PlanPair[] = [];

    let enterpriseCardAdded = false;


    if (myPlanSlug === "student_npo") {
      planPairs.push(findPlanPair("student_npo"));
    }

    if (myPlanSlug === "affiliate" || myPlanSlug === "annual_affiliate" || myPlanSlug === "yearly_affiliate" || canHaveAffiliatePlan) {
      planPairs.push(findPlanPair("affiliate"));
    }

    planPairs.push(findPlanPair("hobby_2"));

    planPairs.push(findPlanPair("startup_2"));

    planPairs.push(findPlanPair("business_2"));

    if (myPlanSlug === "annual_professional_1") {
      planPairs.push(findPlanPair("professional_1"));
    } else {
      planPairs.push(findPlanPair("scaling_202604"));
    }

    planPairs.push(findPlanPair("professional_202604"));
    planPairs.push(findPlanPair("advanced_202604"));

    if (myPlanSlug && (myPlanSlug === activeSubscription?.planSlug) && (isUsingChargebeeCustomFields(myPlanSlug))) {
      planPairs.push({
        monthly: {
          planSlug: myPlanSlug,
          planName: activeSubscription.planName || "Enterprise",
          price: activeSubscription.price,
          currencyCode: activeSubscription.currencyCode,
          billingPeriod: activeSubscription.billingPeriod,
          billingPeriodUnit: activeSubscription.billingPeriodUnit,
          apiCreditLimit: activeSubscription.apiCreditLimit,
          apiConcurrencyLimit: activeSubscription.apiConcurrencyLimit,
          apiGeotargeting: activeSubscription.apiGeotargeting,
          customizable: activeSubscription.customizable
        }
      });
      enterpriseCardAdded = true;
    }

    if (scheduledSubscription && (scheduledSubscription.planSlug !== myPlanSlug) && scheduledSubscription.customizable) {
      planPairs.push({
        monthly: {
          planSlug: scheduledSubscription.planSlug,
          planName: scheduledSubscription.planName,
          price: scheduledSubscription.price,
          currencyCode: scheduledSubscription.currencyCode,
          billingPeriod: scheduledSubscription.billingPeriod,
          billingPeriodUnit: scheduledSubscription.billingPeriodUnit,
          apiCreditLimit: scheduledSubscription.apiCreditLimit,
          apiConcurrencyLimit: scheduledSubscription.apiConcurrencyLimit,
          apiGeotargeting: scheduledSubscription.apiGeotargeting
        }
      });
      enterpriseCardAdded = true;
    }

    if (!enterpriseCardAdded && allPlans) {
      planPairs.push({
        monthly: {
          planSlug: "enterprise",
          planName: "Enterprise",
          price: 0
        }
      });
    }

    return planPairs;
  }, [ myPlanSlug, activeSubscription, scheduledSubscription, findPlanPair, allPlans, canHaveAffiliatePlan ]);

  const getPlan = useCallback((planSlug: string | undefined) => {
    if (planSlug) {
      const planPair = plansToShow.find(planPair => (planPair.monthly?.planSlug === planSlug) || (planPair.annual?.planSlug === planSlug) || (planPair.yearly?.planSlug === planSlug));
      if (planPair) {
        return planPair.monthly?.planSlug === planSlug ? planPair.monthly :
          (planPair.annual?.planSlug === planSlug ? planPair.annual : planPair.yearly);
      }
    }

    return undefined;
  }, [ plansToShow ]);

  const maxCreditsOfAvailablePlans = useMemo(() => {
    return _.max(plansToShow?.map(pp => {
      const planAttributes = pp.monthly ? PlanAttributes[pp.monthly.planSlug!] : undefined;
      return planAttributes?.credits || 0;
    }) || [ 0 ]);
  }, [ plansToShow ]);

  const getPlanIdx = useCallback((planSlug: string | undefined) => {
    const idx = planSlug ? (plansToShow.findIndex(planPair => ((planPair.monthly?.planSlug === planSlug) || (planPair.annual?.planSlug === planSlug) || (planPair.yearly?.planSlug === planSlug))) + 1) : -1;
    return idx * ((isLegacyAnnualPlan(planSlug) || isYearlyPlan(planSlug)) ? 100 : 1);
  }, [ plansToShow ]);

  const myPlan = useMemo(() => {
    return getPlan(myPlanSlug);
  }, [ myPlanSlug, getPlan ]);

  const myPlanIdx = useMemo(() => {
    return getPlanIdx(myPlan?.planSlug);
  }, [ getPlanIdx, myPlan ]);

  const getPricingPlanPropsFor = useCallback((plan: PlanPairPlanType) => {
    const discountPrice = getDiscountPriceFor(plan, plan.defaultCoupons);
    const outOfSync = (myPlan && activeSubscription && (myPlan.planSlug !== activeSubscription.planSlug)) || false;
    const renewing = Boolean(activeSubscription?.lastRenewalAttempt);
    const scheduledCancellation = activeSubscription?.scheduledSubscription?.planSlug === "free";
    const pendingChanges = outOfSync || renewing;

    const planIdx = getPlanIdx(plan.planSlug);

    const props: PricingPlanProps = {
      name: plan.planName,
      price: discountPrice || plan.price,
      originalPrice: plan.price,
      period: plan.billingPeriod || 1,
      periodUnit: plan.billingPeriodUnit,
      planSlug: plan.planSlug,
      isAnnual: (isLegacyAnnualPlan(plan.planSlug) || isYearlyPlan(plan.planSlug)),
      planAutoRenewal: user.planAutoRenewal,
      theme: "secondary",
      buttonAction: "contact_sales",
      buttonClassName: "button button-secondary",
      maxCreditsOfAvailablePlans,
      planIdx,
      planInActionIdx,
      setPlanInActionIdx
    };

    if (plan.planSlug === myPlan?.planSlug) {
      // rendering the active plan (the one in our database)

      props.customPlanOverride = getCustomEnterpriseFeatures(myPlan);

      // user has unpaid invoices
      if (user.hasPaymentIssues) {
        props.buttonAction = "pay_now";
        props.buttonClassName = "button button-primary destructive";
        props.bannerLabel = "Outstanding invoices";

        return props;
      }

      props.buttonAction = [];
      if (pricingOptions?.["auto-upgrade"]?.allowed) {
        props.buttonAction.push("auto_upgrade");
      }
      if (pricingOptions?.["pay-as-you-go"]?.allowed) {
        props.buttonAction.push("pay_as_you_go");
      }
      if (pricingOptions?.renewal?.allowed || (props.buttonAction.length === 0)) {
        if (isYearlyPlan(plan.planSlug)) {
          // for new yearly annual plans, renewal is replaced by the resubscribe feature
          props.buttonAction.push("resubscribe");
        } else {
          // all other plans still supporting the renewal feature
          props.buttonAction.push("renew");
        }
      }

      if (!pricingOptions?.renewal?.allowed && ((props.buttonAction.length === 1) && (props.buttonAction)[0] === "renew")) {
        // renewal disabled and it's the only available action
        props.buttonDisabled = true;
        props.buttonClassName = "button button-primary";
        props.buttonTooltip = <TaggedText message={ Messages.renewalDisabledTaggedMessage } />;
        props.buttonTooltipClickable = true;
      } else if (scheduledCancellation) {
        props.theme = "warning";
        props.buttonClassName = "button button-primary";
        props.bannerLabel = "Cancelling on " + fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000));
      } else if (pendingChanges) {
        // pending changes (chargebee and database out of sync)
        props.theme = "warning";

        if (renewing) {
          props.bannerLabel = "Renewing now";
        } else {
          const chargebeePlanIdx = getPlanIdx(activeSubscription?.planSlug);
          props.bannerLabel = (chargebeePlanIdx < 0 ? "Change" : ((myPlanIdx < chargebeePlanIdx) ? "Upgrade" : "Downgrade")) + " in progress";
        }
        props.buttonDisabled = true;
      } else {
        props.theme = "primary";
        props.buttonClassName = "button button-primary";
      }
    } else {
      // rendering a different plan

      const planIdx = getPlanIdx(plan.planSlug);
      if (plan.planSlug === scheduledSubscription?.planSlug) {
        const scheduledPlanIdx = getPlanIdx(scheduledSubscription.planSlug);
        props.theme = "primary";
        if (plan.customizable) {
          props.customPlanOverride = getCustomEnterpriseFeatures(scheduledSubscription);
          props.bannerLabel = "Scheduled upgrade";
        } else {
          props.buttonAction = "cancel_change";
          props.bannerLabel = "Scheduled " + ((scheduledPlanIdx < myPlanIdx) ? "downgrade" : "upgrade");
        }
      } else if (planIdx >= 0) {
        if (hasToContactUsBeforeChange(plan?.planSlug)) {
          // rendering the Enterprise card
          if (isEnterprise(plan?.planSlug) && !isLegacyAnnualPlan(plan?.planSlug)) {
            props.bannerLabel = "SAVE 10% ON YEARLY PLAN";
          }
        } else if (hasToContactUsBeforeChange(myPlan?.planSlug)) {
          props.buttonAction = "downgrade_enterprise";
        } else if (planIdx < myPlanIdx) {
          props.buttonAction = isLegacyAnnualPlan(activeSubscription?.planSlug) ? "downgrade_annual" : "downgrade";
        } else {
          props.buttonAction = hasToContactUsBeforeChange(plan?.planSlug) ? "contact_sales" : (isLegacyAnnualPlan(activeSubscription?.planSlug) ? "upgrade_annual" : "upgrade");
        }
      }

      if (
        user.hasPaymentIssues ||
        pendingChanges ||
        (user.isBlocked && (user.blockingCode !== "banned_from_free" || (_.intersection([ "contact_sales", "upgrade", "upgrade_annual" ], _.flatten([ props.buttonAction ])).length === 0)))
      ) {
        props.buttonDisabled = true;
      }
    }

    return props;
  }, [ myPlan, myPlanIdx, activeSubscription, scheduledSubscription, user, getPlanIdx, planInActionIdx, setPlanInActionIdx, maxCreditsOfAvailablePlans, pricingOptions ]);

  const showAnnualUserInList = useFeatureSwitch("REACT_APP_ANNUAL_PLANS_USERS");
  const showAnnualNewUser = useFeatureSwitch("REACT_APP_ANNUAL_PLANS_NEW_SIGNUPS_AFTER");
  const showAnnualPrices = isLegacyAnnualPlan(activeSubscription?.planSlug) || showAnnualUserInList || showAnnualNewUser;

  const monthlyOrAnnual = localStorage.getItem("monthlyOrAnnualBilling") as BillingPeriod || "monthly";
  const [ showingPrices, setShowingPrices ] = useState<BillingPeriod>(monthlyOrAnnual);
  const [ activeButton, setActiveButton ] = useState<BillingPeriod>(monthlyOrAnnual);

  useEffect(() => {
    localStorage.setItem("monthlyOrAnnualBilling", activeButton);
  }, [ activeButton ]);

  return (
    <>
      { showAnnualPrices && (
        <div className="flex flex-col gap-y-3">
          <div className="flex justify-end gap-x-1.5">
            <AnnualArrowIcon className="translate-y-2.5"/>
            <span className="text-sm text-gray dark:text-neutral-600">10% off</span>
          </div>
          <div className="flex justify-between">
            <span className="text-lg text-brandDarkest dark:text-primary-800">Plans & pricing</span>
            <div className="flex flex-row">
              <Button
                text="Monthly billing"
                className={ cx("button", activeButton === "monthly" ? "button-primary" : "button-secondary") }
                focusable={false}
                onClick={() => {
                  if (showingPrices === "annual") {
                    setActiveButton("monthly");
                    setShowingPrices("transitioning");
                  }
                }}
              />
              <Button
                text="Annual billing"
                className={ cx("button", activeButton === "annual" ? "button-primary" : "button-secondary") }
                focusable={false}
                onClick={() => {
                  if (showingPrices === "monthly") {
                    setActiveButton("annual");
                    setShowingPrices("transitioning");
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] auto-rows-[1fr] gap-4">
        {
          allPlans &&
          plansToShow.map((planPair) => {
            if (planPair.monthly) {
              const transitionClassName = cx(
                "transition",
                "data-[closed]:opacity-0",
                "data-[enter]:duration-500 data-[enter]:data-[closed]:scale-y-50",
                "data-[leave]:duration-300 data-[leave]:data-[closed]:scale-y-[.2]"
              );

              return (
                <div key={planPair.monthly.planSlug} className="min-w-[300px] min-h-[385px] h-full">
                  {(showAnnualPrices && (planPair.annual || planPair.yearly)) && (
                    <>
                      {/* TODO transition properties have been moved from the <Transition> component to data attributes of the child components */}
                      <Transition
                        show={showingPrices === "monthly"}
                        afterLeave={() => setShowingPrices("annual")}
                      >
                        <div className={ transitionClassName }>
                          <PricingPlan {...getPricingPlanPropsFor(planPair.monthly)} />
                        </div>
                      </Transition>
                      <Transition
                        show={showingPrices === "annual"}
                        afterLeave={() => setShowingPrices("monthly")}
                      >
                        <div className={ transitionClassName }>
                          {/* grandfathering old annual plans */}
                          { (planPair.annual && (planPair.annual.planSlug === myPlan?.planSlug)) && (
                            <PricingPlan {...getPricingPlanPropsFor(planPair.annual)} />
                          ) }

                          {/* yearly plans not available yet */}
                          { (!planPair.yearly && planPair.annual && (planPair.annual.planSlug !== myPlan?.planSlug)) && (
                            <PricingPlan {...getPricingPlanPropsFor(planPair.annual)} />
                          ) }

                          {/* render new yearly plans */}
                          { (planPair.yearly && (!planPair.annual || (planPair.annual.planSlug !== myPlan?.planSlug))) && (
                            <PricingPlan {...getPricingPlanPropsFor(planPair.yearly)} />
                          ) }
                        </div>
                      </Transition>
                    </>
                  )}

                  {(!showAnnualPrices || (!planPair.annual && !planPair.yearly)) && (
                    <PricingPlan {...getPricingPlanPropsFor(planPair.monthly)} />
                  )}
                </div>
              );
            } else {
              return <></>;
            }
          })
        }
      </div>
    </>
  );

};
